import React, { useRef, useState } from 'react';
import BigNumber from 'bignumber.js';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { currencyFormat, datetimeFormat, numberFormat } from '@tokenyico/design-system';
import {
  DsButton,
  DsDialog,
  DsSplitTag,
  DsIcon,
  DsTokenSubscription,
  DsTooltip,
  DsNumber,
} from '@tokenyico/design-system/dist/reactJS';

import { InvestorStatusBadge } from 'app/components';
import TransferToken from './TransferToken';
import placeholderImage from 'assets/images/placeholder.png';
import { hasValuation } from 'app/helpers/hasValuation';
import RedemptionView from '../Redemption/RedemptionView';

const DashboardOverview = ({
  history,
  balance,
  token,
  status,
  subscriptions,
  redemptions,
  jwt,
  lastValuationDate,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef();
  const redeemDialogRef = useRef();
  const amountRef = useRef(null);
  const walletRef = useRef(null);
  const tokenSubscriptionRef = useRef(null);
  const isPaused = get(token, 'isPaused', false);
  const [isRedemptionOpen, setIsRedemptionOpen] = useState(false);

  const now = Date.now();
  let availableSubscriptionInterval = false;
  let nextInterval = false;
  let previousInterval = false;
  if (subscriptions && subscriptions[0] && subscriptions[0].intervals) {
    for (const interval of subscriptions[0].intervals) {
      // check if there is a currently open interval
      if (Date.parse(interval.startDate) < now) {
        // within start
        if (!interval.endDate || Date.parse(interval.endDate) > now) {
          // no end or within end
          availableSubscriptionInterval = true;
          break;
        }
      }
      if (Date.parse(interval.endDate) < now) {
        previousInterval = true;
      }
    }
    if (!availableSubscriptionInterval) {
      // find the next available interval
      const futureIntervals = subscriptions[0].intervals.filter(
        (interval) => Date.parse(interval.startDate) > now,
      );

      futureIntervals.sort((a, b) => (Date.parse(a.startDate) > Date.parse(b.startDate) ? 1 : -1));
      if (futureIntervals.length) nextInterval = futureIntervals[0];
    }
  }

  let displayRedeemButton = false;
  if (redemptions && redemptions.length && (balance?.unfrozenBalance ?? '0') !== '0') {
    displayRedeemButton =
      Date.parse(redemptions[0].startDate) < now &&
      (!redemptions[0].endDate || Date.parse(redemptions[0].endDate) > now);
  }

  const investButton = (
    <DsButton
      version="primary"
      label={t('shared.invest')}
      type="button"
      isDisabled={!availableSubscriptionInterval}
      onClick={() => tokenSubscriptionRef.current.open()}
      className="_m:left(3)"
    />
  );

  return (
    <>
      <div className="dashboard-overview _p:x(2)">
        <div className="_flex:justify(space-between) _flex:center _m:bottom(3)">
          <h2 className="h5">{t('shared.overview')}</h2>
          <div className="_flex-align-center">
            <DsButton
              isDisabled={!balance || (balance && balance.unfrozenBalance <= 0) || isPaused}
              version="monochrome"
              label={t('shared.transferTokens')}
              type="button"
              icon="transfer"
              isReverse={true}
              iconSize="13px"
              onClick={() => dialogRef.current.open()}
            />
            {displayRedeemButton ? (
              <DsButton
                className="_m:left(3)"
                isDisabled={!balance || (balance && balance.unfrozenBalance <= 0)}
                version="monochrome"
                label={t('shared.tokenRedemption')}
                type="button"
                icon="undo"
                isReverse={true}
                iconSize="13px"
                onClick={() => {
                  setIsRedemptionOpen(true);
                  redeemDialogRef.current.open();
                }}
              />
            ) : (
              ''
            )}
            {availableSubscriptionInterval ? (
              investButton
            ) : nextInterval ? (
              <DsTooltip
                text={t('shared.nextInterval', {
                  start_date: datetimeFormat(new Date(Date.parse(nextInterval.startDate))),
                  end: nextInterval.endDate
                    ? t('shared.to') +
                      ' ' +
                      datetimeFormat(new Date(Date.parse(nextInterval.endDate)))
                    : '',
                })}
                style={{ display: 'inline-block' }}
                position="left"
              >
                {investButton}
              </DsTooltip>
            ) : previousInterval ? (
              <DsTooltip
                text={t('shared.subscriptionEnded')}
                style={{ display: 'inline-block' }}
                position="left"
              >
                {investButton}
              </DsTooltip>
            ) : (
              ''
            )}
          </div>
        </div>
        <div className="dashboard-overview__container">
          {token && (
            <div className="dashboard-overview__item">
              <div className="_flex:row _flex:justify(center) _flex:align(center) _px-3">
                <img
                  src={token.image || placeholderImage}
                  alt={t('shared.tokenLogo')}
                  height={42}
                  className="_m:right(3)"
                />

                <div className="_flex:column ">
                  <h3 className="h1 _fontw-500">{token.symbol}</h3>
                  <span className="_text:reg _text-coolGrey-500 _p:right(3)">{token.name} </span>
                </div>
              </div>
            </div>
          )}
          <div className="dashboard-overview__item">
            <div className="_flex:column _flex-center _ptop-1">
              <div className="_text:xs _text-coolGrey-400">{t('shared.yourStatus')}</div>
              {status ? <InvestorStatusBadge status={status} className="_mtop-2" /> : '-'}
            </div>
          </div>
          <div className="dashboard-overview__item">
            <div className="_flex:column _flex:justify(center) _flex:align(center)">
              <span className="_text:xs _text-coolGrey-400">{t('shared.yourBalance')}</span>
              <div className="_flex-center _flex-column _mtop-1">
                {balance ? (
                  <>
                    <h1 className="h4 _text(500)">
                      <DsNumber number={balance.balance} />
                    </h1>
                    {hasValuation(token) ? (
                      <>
                        <span className="_text-coolGrey-500 _text-xs">
                          <DsNumber
                            number={new BigNumber(balance.balance)
                              .multipliedBy(token.lastValuationPrice.valuation)
                              .toNumber()}
                            currency={token.currency}
                          />
                        </span>
                      </>
                    ) : (
                      ''
                    )}
                  </>
                ) : (
                  <span className="h6">-</span>
                )}
              </div>
            </div>
          </div>

          <div className="dashboard-overview__item">
            <div className="_flex:column _flex:justify(center) _flex:align(center)">
              <div className="_text:xs _text-coolGrey-400 _mbottom-1">
                {t('shared.blocked')} / {t('shared.unblocked')}
              </div>
              {balance ? (
                <div className="_flex:column _flex:justify(center) _flex:align(center)">
                  <DsSplitTag
                    leftValue={numberFormat(balance.frozenBalance || 0, token.decimals)}
                    rightValue={numberFormat(balance.unfrozenBalance || 0, token.decimals)}
                    leftIcon="lock"
                    rightIcon="verified"
                    isFixedWidth
                    isCopyable
                    size="xs"
                    className="_p-0"
                  />

                  {hasValuation(token) ? (
                    <div
                      style={{ flexWrap: 'wrap' }}
                      className="_flex-row _flex-align-center _mtop-2"
                    >
                      <span className="_text:xs _text-coolGrey-500 _mright-2 _text-overflow:ellipsis">
                        <DsIcon size="12px" name="lock" color="var(--red-300)" />{' '}
                        {currencyFormat(
                          new BigNumber(balance.frozenBalance).multipliedBy(
                            token.lastValuationPrice.valuation,
                          ),
                          token.currency,
                        )}
                      </span>
                      <span className="_text:xs _text-coolGrey-500 _text-overflow:ellipsis">
                        <DsIcon size="12px" name="verified" color="var(--green-400)" />{' '}
                        {currencyFormat(
                          new BigNumber(balance.unfrozenBalance).multipliedBy(
                            token.lastValuationPrice.valuation,
                          ),
                          token.currency,
                        )}
                      </span>
                    </div>
                  ) : (
                    ''
                  )}
                </div>
              ) : (
                '-'
              )}
            </div>
          </div>
        </div>
      </div>

      <DsDialog isModal={true} ref={dialogRef} isDismissible={true}>
        <TransferToken
          token={token}
          balance={balance}
          forwardedAmountRef={amountRef}
          forwardedWalletRef={walletRef}
          forwardedDialogRef={dialogRef}
          onCloseModal={() => {
            dialogRef.current.close();
          }}
        />
      </DsDialog>
      <DsDialog
        id="redemption-dialog"
        isModal={true}
        ref={redeemDialogRef}
        isDismissible={true}
        onClosed={() => setIsRedemptionOpen(false)}
      >
        <div>
          {isRedemptionOpen ? (
            <RedemptionView
              redemptionId={redemptions[0].id}
              redemption={redemptions[0]}
              lastValuationDate={lastValuationDate}
              token={token}
              balance={balance}
              onCloseModal={() => {
                setIsRedemptionOpen(false);
                redeemDialogRef.current.close();
              }}
            />
          ) : (
            ''
          )}
        </div>
      </DsDialog>

      <DsTokenSubscription
        ref={tokenSubscriptionRef}
        servicingApi={process.env.REACT_APP_API_URL_SERVICING}
        tokenId={token.id}
        jwt={jwt}
        onClosed={({ detail }) => {
          if (detail) history.push('/orders/subscriptions');
        }}
      />
    </>
  );
};

export default DashboardOverview;
